<template>
  <div class="player-layout">
    <PlayerTopNav />

    <!-- Country-blocked full-page notice -->
    <div v-if="countryBlocked" class="main-content">
      <JurisdictionBlockNotice
        type="country"
        :message="countryBlockMessage"
      />
    </div>

    <template v-else>
      <div class="body-row">
        <PlayerSidebar @toggle="onSidebarToggle" />
        <main class="main-content">
          <router-view />
        </main>
      </div>
      <MobileBottomNav />
    </template>

    <SessionReminderPopup
      v-if="sessionReminderMinutes > 0 && !countryBlocked"
      :reminder-minutes="sessionReminderMinutes"
      @logout="handleLogout"
    />

    <PushPermissionPrompt v-if="!countryBlocked" />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import PlayerTopNav from './PlayerTopNav.vue'
import PlayerSidebar from './PlayerSidebar.vue'
import MobileBottomNav from './MobileBottomNav.vue'
import SessionReminderPopup from './SessionReminderPopup.vue'
import JurisdictionBlockNotice from './JurisdictionBlockNotice.vue'
import PushPermissionPrompt from './PushPermissionPrompt.vue'
import apiClient from '@/api/client'
import { useJurisdiction } from '@/composables/useJurisdiction'

const router = useRouter()
const sidebarCollapsed = ref(false)
const sessionReminderMinutes = ref(0)
const countryBlocked = ref(false)
const countryBlockMessage = ref<string | undefined>(undefined)

const { fetchJurisdiction } = useJurisdiction()

function onSidebarToggle(collapsed: boolean) {
  sidebarCollapsed.value = collapsed
}

function handleLogout() {
  // Navigate to lobby (auth logout will be handled by auth system when implemented)
  router.push('/player/lobby')
}

onMounted(async () => {
  try {
    const { data } = await apiClient.get('/api/settings/public')
    sessionReminderMinutes.value = data.session_reminder_minutes ?? 30
  } catch {
    // Default to 30 minutes if settings fetch fails
    sessionReminderMinutes.value = 30
  }

  // Check jurisdiction — show full-page notice if the player's country is blocked
  const jurisdiction = await fetchJurisdiction()
  if (jurisdiction?.is_blocked) {
    countryBlocked.value = true
    // Extract message from the blocked_country rule if present
    countryBlockMessage.value = undefined
  }
})
</script>

<style scoped>
.player-layout {
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
  background: var(--bg-primary, #0f1923);
  color: var(--text-primary, #fff);
}

.body-row {
  display: flex;
  flex: 1;
  overflow: hidden;
}

.main-content {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
}

.main-content :deep(.view) {
  padding: 24px;
  animation: fadeUp 0.3s ease both;
}

@keyframes fadeUp {
  from {
    opacity: 0;
    transform: translateY(16px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Custom scrollbar */
.main-content::-webkit-scrollbar {
  width: 4px;
  height: 4px;
}

.main-content::-webkit-scrollbar-track {
  background: var(--bg-primary, #0f1923);
}

.main-content::-webkit-scrollbar-thumb {
  background: var(--bg-tertiary, #213743);
  border-radius: 4px;
}

/* Mobile: account for fixed bottom nav */
@media (max-width: 767px) {
  .main-content {
    padding-bottom: calc(var(--bottom-nav-height, 56px) + env(safe-area-inset-bottom, 0));
  }
}
</style>
